import type { FormConfig } from '../form-engine.js';
import { VERTICAL_KEYS } from '../vertical.js';

// =============================================================================
// FormConfig that mirrors the legacy MCMV qualification form 1:1.
//
// Field IDs match the keys in `qualificationAnswers` (the MCMV strict shape),
// so a payload submitted from this form is structurally indistinguishable from
// what the legacy `qualification-form.tsx` produces. That keeps the MCMV
// qualifier's parse step green and lets us cutover by swapping the page
// component, no backend change required.
//
// Lives in @nexofacil/schemas so both the FE renderer and the API-side parity
// tests can consume the exact same artifact. Strings are inlined here (PT-BR)
// rather than imported from apps/web/src/messages.ts so this package stays
// dependency-free · the FE can keep its own messages catalog for hero/FAQ/etc.
//
// Visual paridade:
//   - Step 1 = contact (lead-scope: name, email, phone)
//   - Step 2 = income, source, IR (conditional on self_employed), age,
//     dependents, marital status
//   - Step 3 = FGTS, FGTS balance (conditional on hasFGTS), property, MCMV
//     history, credit
//   - Step 4 = down payment, timeline
//
// Known UX divergences vs the legacy form:
//   - The hint "comprovação por extrato bancário + IR" used to appear only
//     when the user selected "Renda informal". The engine has no support for
//     option-level hints (only field-level), so the hint here is shown as a
//     constant on the income-source field.
//   - Age (number) and Dependents (stepper) rendered side-by-side in a 2-col
//     grid in the legacy form. The engine renders one row per field · minor
//     vertical-rhythm change, not a content change.
// =============================================================================

export const mcmvFormConfig: FormConfig = {
  version: 1,
  verticalKey: VERTICAL_KEYS.MCMV_IMOBILIARIO,
  submitLabel: 'Finalizar e ir pro WhatsApp',
  steps: [
    {
      id: 'contact',
      title: 'Pra começar, seu contato',
      subtitle:
        'No final, o WhatsApp abre com a sua mensagem pronta pra quem vai te atender.',
      fields: [
        {
          id: 'name',
          scope: 'lead',
          type: 'text',
          label: 'Nome completo',
          placeholder: 'Maria Silva',
          validation: { required: true, minLength: 2, maxLength: 160 },
        },
        {
          id: 'email',
          scope: 'lead',
          type: 'email',
          label: 'E-mail',
          placeholder: 'maria@email.com',
          validation: { required: true },
        },
        {
          id: 'phone',
          scope: 'lead',
          type: 'phone',
          label: 'WhatsApp (com DDD)',
          placeholder: '(48) 99999-9999',
          validation: { required: true },
        },
      ],
    },
    {
      id: 'income',
      title: 'Sobre sua renda',
      subtitle:
        'A renda familiar define em qual faixa do MCMV você se enquadra. Suas respostas não são compartilhadas com bancos.',
      fields: [
        {
          id: 'monthlyIncomeCents',
          type: 'money-bracket',
          label: 'Renda mensal total da família',
          // Brackets calibrated to Angel/Wesley's MRV portfolio (imóveis a
          // partir de ~R$ 250k → exige ~R$ 5k de renda familiar). The old
          // 5-bracket layout split Faixa 1 and Faixa 2 of MCMV, but the
          // midpoint of "R$ 3.200 – 5.000" (R$ 4.100) didn't tell us where
          // the lead actually fell inside that range · so we collapsed to
          // a single "até R$ 5.000" bucket. Anything below 500_000 cents
          // triggers income_below_min in the qualifier.
          validation: { required: true, min: 1 },
          options: [
            { value: 400_000, label: 'Até R$ 5.000' },
            { value: 730_000, label: 'R$ 5.000 – R$ 9.600', description: 'Faixa 3 do MCMV' },
            { value: 1_130_000, label: 'R$ 9.600 – R$ 13.000', description: 'Faixa 4 do MCMV' },
            { value: 1_500_000, label: 'Acima de R$ 13.000' },
          ],
        },
        {
          id: 'incomeSource',
          type: 'single-choice',
          label: 'Como você comprova renda?',
          hint: 'Renda informal: comprovação por extrato bancário + declaração de IR.',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'clt', label: 'CLT (carteira assinada)', iconName: 'Briefcase' },
            { value: 'self_employed', label: 'Autônomo/MEI', iconName: 'Wallet' },
            { value: 'business_owner', label: 'Empresário', iconName: 'Building2' },
            { value: 'retired', label: 'Aposentado / pensionista', iconName: 'HeartHandshake' },
            { value: 'informal', label: 'Renda informal', iconName: 'Coins' },
            { value: 'other', label: 'Outro', iconName: 'CircleHelp' },
          ],
        },
        {
          id: 'hasIncomeTaxDeclaration',
          type: 'yes-no',
          label: 'Você já declarou imposto de renda?',
          hint: 'Pra autônomos e MEI, a Caixa exige comprovação de renda via IR (Imposto de Renda) ou DECORE.',
          validation: { required: true },
          condition: { field: 'incomeSource', equals: 'self_employed' },
        },
        {
          id: 'age',
          type: 'number',
          label: 'Idade',
          validation: { required: true, min: 18, max: 110 },
        },
        {
          id: 'dependents',
          type: 'stepper',
          label: 'Quantos dependentes?',
          validation: { required: true, min: 0, max: 20 },
        },
        {
          id: 'maritalStatus',
          type: 'single-choice',
          label: 'Estado civil',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'single', label: 'Solteiro(a)' },
            { value: 'married', label: 'Casado(a)' },
            { value: 'stable_union', label: 'União estável' },
            { value: 'divorced', label: 'Divorciado(a)' },
            { value: 'widowed', label: 'Viúvo(a)' },
          ],
        },
      ],
    },
    {
      id: 'credit',
      title: 'Crédito e FGTS',
      subtitle:
        'Essas perguntas ajudam a estimar se o financiamento sai. Tudo bem se você não souber algum item.',
      fields: [
        {
          id: 'hasFGTS',
          type: 'yes-no',
          label: 'Você tem saldo de FGTS?',
          validation: { required: true },
        },
        {
          id: 'fgtsBalanceCents',
          type: 'money-bracket',
          label: 'Saldo aproximado de FGTS',
          condition: { field: 'hasFGTS', equals: true },
          validation: { required: true },
          options: [
            { value: 250_000, label: 'Até R$ 5.000' },
            { value: 1_000_000, label: 'R$ 5.000 – R$ 15.000' },
            { value: 2_250_000, label: 'R$ 15.000 – R$ 30.000' },
            { value: 4_000_000, label: 'Mais de R$ 30.000' },
          ],
        },
        {
          id: 'ownsProperty',
          type: 'yes-no',
          label: 'Possui imóvel próprio onde mora ou trabalha?',
          validation: { required: true },
        },
        {
          id: 'hasUsedMCMVBefore',
          type: 'yes-no',
          label: 'Você já comprou algum imóvel pelo MCMV?',
          validation: { required: true },
        },
        {
          id: 'creditStatus',
          type: 'single-choice',
          label: 'Como está seu nome no SPC/Serasa?',
          validation: { required: true },
          options: [
            {
              value: 'clean',
              label: 'Limpo',
              description: 'Sem dívidas em aberto no SPC/Serasa',
              iconName: 'CheckCircle2',
            },
            {
              value: 'restricted',
              label: 'Com restrição',
              description: 'Tenho alguma pendência ativa',
              iconName: 'AlertCircle',
            },
            {
              value: 'unknown',
              label: 'Não sei',
              description: 'Não tenho certeza',
              iconName: 'HelpCircle',
            },
          ],
        },
      ],
    },
    {
      id: 'final',
      title: 'Quase lá',
      subtitle: 'Últimos detalhes pra orientar o melhor próximo passo.',
      fields: [
        {
          id: 'downPaymentAvailableCents',
          type: 'money-bracket',
          label: 'Quanto você tem disponível?',
          validation: { required: true },
          options: [
            { value: null, label: 'Não tenho ainda' },
            { value: 250_000, label: 'Até R$ 5.000' },
            { value: 1_000_000, label: 'R$ 5.000 – R$ 15.000' },
            { value: 2_250_000, label: 'R$ 15.000 – R$ 30.000' },
            { value: 4_000_000, label: 'Mais de R$ 30.000' },
          ],
        },
        {
          id: 'timeline',
          type: 'single-choice',
          label: 'Quando pretende fechar?',
          validation: { required: true },
          options: [
            {
              value: 'within_1_month',
              label: 'Até 1 mês',
              description: 'Quero comprar logo',
              iconName: 'CalendarCheck2',
            },
            {
              value: '1_to_3_months',
              label: '1 a 3 meses',
              description: 'Buscando ativamente',
              iconName: 'CalendarClock',
            },
            {
              value: '3_to_6_months',
              label: '3 a 6 meses',
              description: 'Me organizando',
              iconName: 'CalendarRange',
            },
            {
              value: '6_plus_months',
              label: 'Mais de 6 meses',
              description: 'Estou só pesquisando',
              iconName: 'CalendarDays',
            },
          ],
        },
      ],
    },
  ],
};
